import {
  DEFAULT_FORM_SOURCE,
  FORM_SOURCE,
  FORM_SOURCE_BACK_LABELS,
  FORM_SOURCE_QUERY_PARAM,
  FORM_SOURCE_RETURN_ROUTES,
} from '../constants/form-source.constants';

const KNOWN_FORM_SOURCES = Object.values(FORM_SOURCE);

const isKnownFormSource = (source) => KNOWN_FORM_SOURCES.includes(source);

/**
 * Reads the Forms host module from a router `search` string; unknown or missing values fall
 * back to Feeds so legacy links keep working.
 * @param {string | undefined | null} search - e.g. `?source=reentry-planning`
 * @returns {string} One of {@link FORM_SOURCE}.
 */
export const resolveFormSource = (search) => {
  const value = new URLSearchParams(search ?? '').get(FORM_SOURCE_QUERY_PARAM);
  return isKnownFormSource(value) ? value : DEFAULT_FORM_SOURCE;
};

/**
 * Appends `?source=` to a Forms sub-page path. The default source is omitted so Feeds URLs
 * stay unchanged.
 * @param {string} path
 * @param {string | undefined} source
 * @returns {string}
 */
export const buildFormSourceRoute = (path, source) => {
  if (!isKnownFormSource(source) || source === DEFAULT_FORM_SOURCE) {
    return path;
  }
  const params = new URLSearchParams({ [FORM_SOURCE_QUERY_PARAM]: source });
  return `${path}?${params.toString()}`;
};

/**
 * Forms tab route on the host module (back / save / cancel destination).
 * @param {string | undefined} source
 * @returns {string}
 */
export const resolveFormSourceReturnRoute = (source) =>
  FORM_SOURCE_RETURN_ROUTES[source] ?? FORM_SOURCE_RETURN_ROUTES[DEFAULT_FORM_SOURCE];

/**
 * Back button copy for the host module (e.g. `Back to Re-Entry Planning`).
 * @param {string | undefined} source
 * @returns {string}
 */
export const resolveFormSourceBackLabel = (source) =>
  FORM_SOURCE_BACK_LABELS[source] ?? FORM_SOURCE_BACK_LABELS[DEFAULT_FORM_SOURCE];

/**
 * Lower-cased tab fragment from a URL hash. Under `HashRouter` the raw `window.location.hash`
 * is `#/feeds#forms`, while the router location hash is `#forms`; taking the text after the
 * last `#` handles both.
 * @param {string | undefined | null} hash
 * @returns {string}
 */
export const resolveHashFragment = (hash) => {
  const value = String(hash ?? '');
  return value.slice(value.lastIndexOf('#') + 1).toLowerCase();
};
